import { useDeferredValue, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@cortex/web-design-system/components/ui/Input';
import { useBdReviewers } from '../../../hooks/useBasicDesign.js';

export interface BdReviewerPickerProps {
  /** 入力中のニックネーム */
  value: string;
  /** 入力・候補の選択 */
  onChange: (nickName: string) => void;
}

/**
 * レビュアーをニックネームで選ぶ。打った文字で Firestore の users を探し、候補を下に並べる。
 *
 * 自由に打った名前もそのまま依頼に使えるが、依頼のときに API が users.nickName と突き合わせて
 * 確かめる（知らない名前は断られる）。候補はメールを返さない（必要の無い個人情報を画面に出さない）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 基本設計のレビュアーを Firestore users のニックネームで探して選ぶ
 * @graph-connects useBdReviewers [calls] レビュアーの候補
 */
export function BdReviewerPicker({ value, onChange }: BdReviewerPickerProps) {
  const { t } = useTranslation();
  const listId = useId();
  // 打つたびに探すと 1 文字ごとに読みに行くので、入力が落ち着いてから探す
  const query = useDeferredValue(value);
  const reviewers = useBdReviewers(query);
  const items = (reviewers.data?.items ?? []).filter((item) => item.nickName !== value.trim());

  return (
    <div>
      <Input
        label={t('bd.review.reviewer')}
        size="sm"
        fullWidth
        placeholder={t('bd.review.reviewerPlaceholder')}
        aria-controls={listId}
        autoComplete="off"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
        }}
      />
      {items.length === 0 ? null : (
        <ul
          id={listId}
          aria-label={t('bd.review.candidates')}
          className="mt-1 flex flex-wrap gap-1"
        >
          {items.map((item) => (
            <li key={item.nickName}>
              <button
                type="button"
                className="rounded-full border border-[var(--color-border)] px-2 py-0.5 text-xs text-[var(--color-text-primary)] hover:border-[var(--color-border-hover)] hover:bg-[var(--color-bg-hover)]"
                onClick={() => {
                  onChange(item.nickName);
                }}
              >
                {item.nickName}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
